import React, { useState } from 'react';
import {
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Button,
  Card,
  CardContent,
  Box,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Stack
} from '@mui/material';
import { 
  Receipt,
  TrendingUp,
  TrendingDown,
  Undo,
  CalendarToday
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faReceipt,
  faArrowUp,
  faArrowDown,
  faUndo,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import RefundModal from './RefundModal';

const TransactionListAdmin = ({ transactions, onDataRefresh, isUserView }) => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleOpenModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTransaction(null);
    setIsModalOpen(false);
  };

  const handleRefund = () => {
    onDataRefresh();
    handleCloseModal();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    return type === 'credit' || type === 'credito' ? faArrowUp : faArrowDown;
  };

  const getTransactionColor = (type) => {
    return type === 'credit' || type === 'credito' ? '#4CAF50' : '#F44336';
  };

  const getTransactionLabel = (type) => {
    return type === 'credit' || type === 'credito' ? 'Crédito' : 'Débito';
  };

  const normalizedTransactions = transactions.map((tx, index) => ({
    ...tx,
    id: tx.id || `tx-${index}-${tx.created_at}`,
    amount: parseFloat(tx.amount || tx.total_amount || 0),
    type: tx.type || (tx.total_amount > 0 ? 'credit' : 'debit'),
    timestamp: tx.timestamp || tx.created_at,
    description: tx.description || `Transacción de ${tx.total_amount}`
  }));

  return (
    <Card sx={{
      borderRadius: 3,
      boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
      border: '1px solid rgba(0, 38, 66, 0.06)'
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3
        }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Receipt sx={{ color: '#FFFFFF', fontSize: '1.2rem' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: '#002642',
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}
          >
            {isUserView ? 'Historial de Transacciones del Usuario' : 'Transacciones Generales de la Sede'}
          </Typography>
          {normalizedTransactions && normalizedTransactions.length > 0 && (
            <Chip
              label={`${normalizedTransactions.length} transacción${normalizedTransactions.length !== 1 ? 'es' : ''}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(219, 150, 0, 0.1)',
                color: '#DB9600',
                fontWeight: 600
              }}
            />
          )}
        </Box>

        {!normalizedTransactions || normalizedTransactions.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              backgroundColor: 'rgba(0, 87, 122, 0.05)',
              border: '1px solid rgba(0, 87, 122, 0.1)',
              '& .MuiAlert-icon': { color: '#00577A' }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              No hay transacciones para mostrar
            </Typography>
          </Alert>
        ) : isMobile ? (
          /* Vista móvil - Cards */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {normalizedTransactions.map((tx) => (
              <Card
                key={tx.id}
                sx={{
                  border: '1px solid rgba(0, 38, 66, 0.06)',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 38, 66, 0.1)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          backgroundColor: `${getTransactionColor(tx.type)}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={getTransactionIcon(tx.type)} 
                          style={{ 
                            color: getTransactionColor(tx.type), 
                            fontSize: '0.9rem' 
                          }} 
                        />
                      </Box>
                      <Box>
                        <Chip 
                          label={getTransactionLabel(tx.type)}
                          size="small"
                          sx={{ 
                            backgroundColor: `${getTransactionColor(tx.type)}15`,
                            color: getTransactionColor(tx.type),
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: getTransactionColor(tx.type),
                        fontWeight: 700,
                        fontSize: '1.1rem'
                      }}
                    >
                      ${tx.amount.toFixed(2)}
                    </Typography>
                  </Box>

                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#002642',
                      fontWeight: 500,
                      mb: 1,
                      lineHeight: 1.4
                    }}
                  >
                    {tx.description}
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 1.5, sm: 0 }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FontAwesomeIcon 
                        icon={faCalendarAlt} 
                        style={{ 
                          color: '#002642', 
                          fontSize: '0.7rem' 
                        }} 
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#002642',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}
                      >
                        {formatDate(tx.timestamp)}
                      </Typography>
                    </Box>

                    {isUserView && tx.type === 'debit' && (
                      <Button 
                        variant="outlined"
                        size="small"
                        startIcon={<Undo />}
                        onClick={() => handleOpenModal(tx)}
                        fullWidth={isMobile}
                        sx={{
                          borderColor: '#F44336',
                          color: '#F44336',
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          minWidth: { xs: 'auto', sm: '120px' },
                          '&:hover': {
                            borderColor: '#F44336',
                            backgroundColor: 'rgba(244, 67, 54, 0.04)'
                          }
                        }}
                      >
                        Reembolsar
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          /* Vista desktop - Tabla */
          <TableContainer 
            component={Paper} 
            sx={{ 
              borderRadius: 2,
              boxShadow: 'none',
              border: '1px solid rgba(0, 38, 66, 0.06)',
              overflowX: 'auto'
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 38, 66, 0.02)' }}>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    borderBottom: '2px solid rgba(0, 38, 66, 0.1)',
                    width: '20%'
                  }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    borderBottom: '2px solid rgba(0, 38, 66, 0.1)',
                    width: '15%'
                  }}>
                    Tipo
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    borderBottom: '2px solid rgba(0, 38, 66, 0.1)',
                    width: '45%'
                  }}>
                    Descripción
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    borderBottom: '2px solid rgba(0, 38, 66, 0.1)',
                    width: '15%'
                  }}>
                    Monto
                  </TableCell>
                  {isUserView && (
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: '#002642',
                      borderBottom: '2px solid rgba(0, 38, 66, 0.1)',
                      width: '5%'
                    }}>
                      Acciones
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {normalizedTransactions.map((tx) => (
                  <TableRow
                    key={tx.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(0, 38, 66, 0.02)'
                      }
                    }}
                  >
                    <TableCell sx={{ borderBottom: '1px solid rgba(0, 38, 66, 0.06)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faCalendarAlt} 
                          style={{ 
                            color: '#002642', 
                            fontSize: '0.8rem' 
                          }} 
                        />
                        <Typography variant="body2" sx={{ color: '#002642' }}>
                          {formatDate(tx.timestamp)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(0, 38, 66, 0.06)' }}>
                      <Chip 
                        icon={<FontAwesomeIcon 
                          icon={getTransactionIcon(tx.type)} 
                          style={{ fontSize: '0.7rem' }} 
                        />}
                        label={getTransactionLabel(tx.type)}
                        size="small"
                        sx={{ 
                          backgroundColor: `${getTransactionColor(tx.type)}15`,
                          color: getTransactionColor(tx.type),
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(0, 38, 66, 0.06)' }}>
                      <Typography variant="body2" sx={{ color: '#002642', fontWeight: 500 }}>
                        {tx.description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(0, 38, 66, 0.06)' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: getTransactionColor(tx.type),
                          fontWeight: 600
                        }}
                      >
                        ${tx.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    {isUserView && (
                      <TableCell sx={{ borderBottom: '1px solid rgba(0, 38, 66, 0.06)' }}>
                        {tx.type === 'debit' && (
                          <Button 
                            variant="outlined"
                            size="small"
                            startIcon={<Undo />}
                            onClick={() => handleOpenModal(tx)}
                            sx={{
                              borderColor: '#F44336',
                              color: '#F44336',
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              '&:hover': {
                                borderColor: '#F44336',
                                backgroundColor: 'rgba(244, 67, 54, 0.04)'
                              }
                            }}
                          >
                            Reembolsar
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {isModalOpen && selectedTransaction && (
        <RefundModal
          transaction={selectedTransaction}
          onClose={handleCloseModal}
          onRefund={handleRefund}
        />
      )}
    </Card>
  );
};

export default TransactionListAdmin;